import {
  JSON_LD_TYPE,
  OG_SKU_META_PROPERTIES,
  PRODUCT_SIGNAL_SELECTOR,
  type PageContextPayload,
} from "./page-context"

export const NAVIGATION_DEBOUNCE_MS = 100

export const NAVIGATION_WATCH_BUDGET_MS = 10_000

export const OBSERVER_RECHECK_MS = 250

const OG_SKU_PROPERTIES = new Set(OG_SKU_META_PROPERTIES)

const ELEMENT_NODE = 1

type StateMethod = History["pushState"]

export type CancelDelay = () => void

export interface SoftNavigationScheduler {
  nextFrame(run: () => void): void
  delay(run: () => void, ms: number): CancelDelay
}

export interface MutationNodeLike {
  nodeName?: string
  nodeType?: number
  getAttribute?(name: string): string | null
  querySelector?(selector: string): unknown
  parentNode?: MutationNodeLike | null
}

export interface MutationRecordLike {
  type: string
  target: MutationNodeLike
  addedNodes?: ArrayLike<MutationNodeLike>
  removedNodes?: ArrayLike<MutationNodeLike>
}

export interface DomObserver {
  disconnect(): void
}

export interface SoftNavigationHost {
  history: Pick<History, "pushState" | "replaceState">
  addEventListener(type: "popstate", listener: () => void): void
  removeEventListener(type: "popstate", listener: () => void): void
  scheduler: SoftNavigationScheduler
  observeDocument(
    onMutations: (records: ArrayLike<MutationRecordLike>) => void,
  ): DomObserver | null
}

export function browserSoftNavigationHost(): SoftNavigationHost {
  return {
    history: window.history,
    addEventListener: (type, listener) =>
      window.addEventListener(type, listener),
    removeEventListener: (type, listener) =>
      window.removeEventListener(type, listener),
    scheduler: {
      nextFrame: (run) => {
        if (typeof window.requestAnimationFrame === "function") {
          window.requestAnimationFrame(() => run())
        } else {
          window.setTimeout(run, 0)
        }
      },
      delay: (run, ms) => {
        const id = window.setTimeout(run, ms)
        return () => window.clearTimeout(id)
      },
    },
    observeDocument: (onMutations) => {
      if (typeof MutationObserver === "undefined") return null
      const observer = new MutationObserver((records) =>
        onMutations(records as unknown as ArrayLike<MutationRecordLike>),
      )
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["content", "property", "type"],
      })
      return observer
    },
  }
}

function isJsonLdScript(node: MutationNodeLike | null | undefined): boolean {
  if (!node) return false
  if (node.nodeName?.toUpperCase() !== "SCRIPT") return false
  return node.getAttribute?.("type") === JSON_LD_TYPE
}

function isProductMeta(node: MutationNodeLike | null | undefined): boolean {
  if (!node) return false
  if (node.nodeName?.toUpperCase() !== "META") return false
  const property = node.getAttribute?.("property")
  return typeof property === "string" && OG_SKU_PROPERTIES.has(property)
}

function isProductSignal(node: MutationNodeLike | null | undefined): boolean {
  return isJsonLdScript(node) || isProductMeta(node)
}

function containsProductSignal(node: MutationNodeLike | undefined): boolean {
  if (!node || node.nodeType !== ELEMENT_NODE) return false
  return node.querySelector?.(PRODUCT_SIGNAL_SELECTOR) != null
}

function listHasProductSignal(
  nodes: ArrayLike<MutationNodeLike> | undefined,
): boolean {
  if (!nodes) return false
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i]
    if (isProductSignal(node)) return true
    if (containsProductSignal(node)) return true
  }
  return false
}

export function touchesProductSignal(
  records: ArrayLike<MutationRecordLike>,
): boolean {
  for (let i = 0; i < records.length; i += 1) {
    const record = records[i]
    if (!record) continue
    if (record.type === "characterData") {
      if (isJsonLdScript(record.target?.parentNode)) return true
      continue
    }
    if (isProductSignal(record.target)) return true
    if (record.type === "attributes") continue
    if (listHasProductSignal(record.addedNodes)) return true
    if (listHasProductSignal(record.removedNodes)) return true
  }
  return false
}

function patchHistory(
  history: SoftNavigationHost["history"],
  method: "pushState" | "replaceState",
  onNavigate: () => void,
): () => void {
  const original: StateMethod = history[method]
  const patched: StateMethod = (...args: Parameters<StateMethod>) => {
    const result = original.apply(history, args)
    onNavigate()
    return result
  }
  history[method] = patched
  return () => {
    if (history[method] === patched) history[method] = original
  }
}

function sameContext(a: PageContextPayload, b: PageContextPayload): boolean {
  return a.sku === b.sku && a.pathname === b.pathname
}

export function watchSoftNavigation(
  detect: () => PageContextPayload,
  post: (context: PageContextPayload) => void,
  host: SoftNavigationHost = browserSoftNavigationHost(),
): () => void {
  let disposed = false
  let cycleToken = 0
  let cancelDebounce: CancelDelay | null = null
  let cancelBudget: CancelDelay | null = null
  let observer: DomObserver | null = null
  let cycleContext: PageContextPayload | null = null
  let cancelRecheckWindow: CancelDelay | null = null
  let recheckPending = false
  const pendingDefers = new Set<CancelDelay>()

  const defer = (token: number, run: () => void) => {
    host.scheduler.nextFrame(() => {
      if (disposed || token !== cycleToken) return
      let cancel: CancelDelay = () => {}
      cancel = host.scheduler.delay(() => {
        pendingDefers.delete(cancel)
        if (disposed || token !== cycleToken) return
        run()
      }, 0)
      pendingDefers.add(cancel)
    })
  }

  const stopWatching = () => {
    observer?.disconnect()
    observer = null
    cancelBudget?.()
    cancelBudget = null
    cancelRecheckWindow?.()
    cancelRecheckWindow = null
    recheckPending = false
  }

  const abandonCycle = () => {
    cycleToken += 1
    for (const cancel of pendingDefers) cancel()
    pendingDefers.clear()
    cancelDebounce?.()
    cancelDebounce = null
    stopWatching()
    cycleContext = null
  }

  const startWatching = (token: number) => {
    const recheck = () => {
      const late = detect()
      if (cycleContext && sameContext(cycleContext, late)) return
      cycleContext = late
      post(late)
      stopWatching()
    }

    const openRecheckWindow: () => void = () => {
      cancelRecheckWindow = host.scheduler.delay(() => {
        cancelRecheckWindow = null
        if (disposed || token !== cycleToken || !recheckPending) return
        recheckPending = false
        openRecheckWindow()
        defer(token, recheck)
      }, OBSERVER_RECHECK_MS)
    }

    observer = host.observeDocument((records) => {
      if (disposed || token !== cycleToken) return
      if (!touchesProductSignal(records)) return
      if (cancelRecheckWindow) {
        recheckPending = true
        return
      }
      openRecheckWindow()
      defer(token, recheck)
    })
    cancelBudget = host.scheduler.delay(() => {
      cancelBudget = null
      stopWatching()
    }, NAVIGATION_WATCH_BUDGET_MS)
  }

  const runDetection = (token: number) => {
    const settled = detect()
    cycleContext = settled
    post(settled)
    startWatching(token)
  }

  const onNavigate = () => {
    if (disposed) return
    abandonCycle()
    const token = cycleToken
    cancelDebounce = host.scheduler.delay(() => {
      cancelDebounce = null
      if (disposed || token !== cycleToken) return
      defer(token, () => runDetection(token))
    }, NAVIGATION_DEBOUNCE_MS)
  }

  const restorePush = patchHistory(host.history, "pushState", onNavigate)
  const restoreReplace = patchHistory(host.history, "replaceState", onNavigate)
  host.addEventListener("popstate", onNavigate)

  return () => {
    if (disposed) return
    disposed = true
    abandonCycle()
    restorePush()
    restoreReplace()
    host.removeEventListener("popstate", onNavigate)
  }
}
